import { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Card } from "@tremor/react";
import {
  Play, Loader2, ArrowLeft, Globe, Clock, FileText,
  AlertTriangle, Check, X, Pause, Trash2, CalendarClock,
  Cpu, Gauge, Hourglass,
} from "lucide-react";
import {
  useCrawls, useStartCrawl, useCancelCrawl, usePauseCrawl, useResumeCrawl, useProperty,
  useUpdateProperty, useCrawlProgressStream, formatEtaSeconds,
  type Crawl,
} from "@/lib/queries";
import { cn } from "@/lib/utils";


// ─── Speed presets ─────────────────────────────────────────────────
// Same prebuilt-options style as the max_urls dropdown. Each preset
// bundles mMaxThreads + mUrlRequestsPerSecond.
//
// The capacity tiers map to real-world targets:
//   polite    — sites with strict per-vhost limits (LSWS OverConnSoftLimit,
//               Cloudflare default plans, shared cPanel hosting). Stefan's
//               gregorco.ro tripped at 30/50, dropped to 2/2 to recover.
//   standard  — Frog's stock factory default. Safe almost everywhere.
//   fast      — own infra with WAF whitelisted (nrg.ro tier). Today's
//               default for everything.
//   max       — beefy server, lots of headroom, want it done now.

type SpeedPresetKey = "polite" | "standard" | "brisk" | "fast" | "max";
const SPEED_PRESETS: Record<SpeedPresetKey, {
  threads: number; urlPerSec: number; label: string;
}> = {
  polite:   { threads:  2, urlPerSec:   2, label: "Polite (2 conn, 2/s) — shared / WAF-throttled" },
  standard: { threads:  5, urlPerSec:   5, label: "Standard (5 conn, 5/s) — most sites, safe" },
  // Brisk fills the gap between Standard (5/5) and Fast (30/50) —
  // ~2x Standard concurrency, ~3x its rate. Right for modern hosting
  // with decent capacity but where Fast would trigger LSWS / WAF rate
  // limits. Geometric middle of Standard/Fast.
  brisk:    { threads: 10, urlPerSec:  15, label: "Brisk (10 conn, 15/s) — modern hosting, light WAF" },
  fast:     { threads: 30, urlPerSec:  50, label: "Fast (30 conn, 50/s) — own server, whitelisted" },
  max:      { threads: 60, urlPerSec: 100, label: "Max (60 conn, 100/s) — beefy server" },
};

export default function CrawlList() {
  const { propertyId: rawId } = useParams<{ propertyId: string }>();
  const propertyId = Number(rawId);
  const property = useProperty(propertyId);
  const list = useCrawls(propertyId);
  const start = useStartCrawl(propertyId);
  const navigate = useNavigate();

  const [error, setError] = useState<string | null>(null);
  const [maxUrls, setMaxUrls] = useState<number>(1000);
  // Speed preset = (mMaxThreads, mUrlRequestsPerSecond) bundle. The
  // labels are descriptive; the throttle defaults Stefan picked map
  // directly to LSWS / Cloudflare / own-infra capacity tiers.
  const [speedPreset, setSpeedPreset] = useState<SpeedPresetKey>("fast");

  const onStart = async () => {
    setError(null);
    const speed = SPEED_PRESETS[speedPreset];
    try {
      const r = await start.mutateAsync({
        max_urls: maxUrls,
        max_threads: speed.threads,
        max_url_per_sec: speed.urlPerSec,
      });
      // Refresh list + jump straight to the new crawl's detail page so
      // the user sees the live progress.
      navigate(`/p/${propertyId}/crawl/${r.crawl_id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed to start crawl");
    }
  };

  const items = list.data?.items ?? [];
  const hasRunning = items.some((c) => c.status === "queued" || c.status === "running");

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link
            to={`/p/${propertyId}/overview`}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3 w-3" /> Property overview
          </Link>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">
            Crawls — <span className="font-mono">{property.data?.domain ?? `property #${propertyId}`}</span>
          </h1>
          <p className="text-sm text-muted-foreground">
            Screaming Frog crawls of this property. Each crawl writes per-URL
            data to the substrate so audits + the Pages lens can read it
            without re-crawling.
          </p>
        </div>
        <div className="flex shrink-0 items-end gap-2">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-muted-foreground">
              Max URLs
            </label>
            <select
              value={maxUrls}
              onChange={(e) => setMaxUrls(Number(e.target.value))}
              disabled={hasRunning || start.isPending}
              className="mt-0.5 rounded-md border border-input bg-background px-2 py-1.5 text-xs font-mono"
            >
              <option value={50}>50 (smoke test)</option>
              <option value={200}>200 (quick)</option>
              <option value={1000}>1,000 (test audit)</option>
              <option value={5000}>5,000 (standard)</option>
              <option value={25000}>25,000 (large site)</option>
              <option value={100000}>100,000 (full)</option>
              <option value={500000}>500,000 (everything)</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-muted-foreground">
              Speed
            </label>
            <select
              value={speedPreset}
              onChange={(e) => setSpeedPreset(e.target.value as SpeedPresetKey)}
              disabled={hasRunning || start.isPending}
              title="Crawler concurrency + request rate. Drop to Polite if the site has strict per-vhost limits (LSWS / Cloudflare / shared hosting)."
              className="mt-0.5 rounded-md border border-input bg-background px-2 py-1.5 text-xs"
            >
              {(Object.keys(SPEED_PRESETS) as SpeedPresetKey[]).map(k => (
                <option key={k} value={k}>{SPEED_PRESETS[k].label}</option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={onStart}
            disabled={hasRunning || start.isPending}
            title={hasRunning
              ? "A crawl is already running for this property"
              : `Crawl up to ${maxUrls.toLocaleString()} URLs at ${SPEED_PRESETS[speedPreset].threads} conn / ${SPEED_PRESETS[speedPreset].urlPerSec}/s`}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {start.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            Start crawl
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {list.isLoading && (
        <Card><div className="text-sm text-muted-foreground">Loading…</div></Card>
      )}

      {property.data && (
        <CrawlScheduleCard
          propertyId={propertyId}
          current={property.data.crawl_schedule}
          lastFire={property.data.last_scheduled_crawl_at}
        />
      )}

      {!list.isLoading && items.length === 0 && (
        <Card>
          <div className="space-y-2 text-sm">
            <p className="font-medium">No crawls yet for this property.</p>
            <p className="text-muted-foreground">
              Click <strong>Start crawl</strong> to kick off the first one.
              Crawls take 5 minutes to several hours depending on site size —
              you can navigate away and check back. The audit pipeline reads
              the latest completed crawl, so as soon as one finishes, the
              next audit will see it.
            </p>
          </div>
        </Card>
      )}

      <div className="space-y-3">
        {items.map((c) => <CrawlRow key={c.id} crawl={c} />)}
      </div>
    </div>
  );
}


function CrawlRow({ crawl }: { crawl: Crawl }) {
  const cancel = useCancelCrawl(crawl.id);
  const pause = usePauseCrawl(crawl.id);
  const resume = useResumeCrawl(crawl.id);
  const navigate = useNavigate();

  const onCancel = async () => {
    if (!confirm("Cancel this crawl? Frog will be killed on forge and partial data is lost.")) return;
    try { await cancel.mutateAsync(); } catch { /* surfaced inline */ }
  };
  const onPause = async () => {
    if (!confirm(
      "Pause this crawl?\n\n" +
      "Frog 23.x can't save state mid-crawl from headless mode, so " +
      "Pause is functionally the same as Cancel — but it preserves " +
      "the configuration so Resume can re-launch a fresh crawl with " +
      "identical settings. The exclude patterns + URL dedup mean the " +
      "re-crawl is faster than starting from zero."
    )) return;
    try { await pause.mutateAsync(); } catch { /* surfaced inline */ }
  };
  const onResume = async () => {
    try {
      const r = await resume.mutateAsync();
      // Jump to the new crawl's detail page so the user sees live progress.
      navigate(`/p/${crawl.property_id}/crawl/${r.new_crawl_id}`);
    } catch { /* surfaced inline */ }
  };

  return (
    <Card
      className="cursor-pointer transition-colors hover:border-primary/50"
      onClick={() => navigate(`/p/${crawl.property_id}/crawl/${crawl.id}`)}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-xs">
            <StatusPill status={crawl.status} />
            <span className="text-muted-foreground">#{crawl.id}</span>
            <span className="text-muted-foreground">·</span>
            <span className="text-muted-foreground">{crawl.triggered_by}</span>
          </div>
          <div className="mt-1.5 flex items-center gap-1.5 truncate font-mono text-sm">
            <Globe className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            {crawl.start_url}
          </div>
          <div className="mt-2 grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
            <Mini icon={FileText} label="Pages" value={crawl.pages_found ?? "—"} />
            <Mini icon={AlertTriangle} label="Broken" value={crawl.broken_count ?? "—"} kind={
              (crawl.broken_count ?? 0) > 0 ? "warn" : undefined
            }/>
            <Mini label="No title" value={crawl.missing_title_count ?? "—"} />
            <Mini label="No meta" value={crawl.missing_meta_count ?? "—"} />
          </div>
          {(crawl.status === "queued" || crawl.status === "running") && (
            <LiveProgressStrip crawlId={crawl.id} />
          )}
          {/* Just-finished crawls (within last 60s) — surface what
              the live stream would have shown for super-quick crawls
              that ended before the user could see anything (e.g. the
              gregorco.ro 59-second WAF-blocked run). */}
          {(crawl.status === "completed" || crawl.status === "timed_out" ||
            crawl.status === "failed" || crawl.status === "cancelled") &&
            crawl.finished_at && Date.now() - new Date(crawl.finished_at).getTime() < 60_000 && (
            <LiveProgressStrip crawlId={crawl.id} />
          )}
          <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {crawl.started_at ? formatDateAgo(crawl.started_at) : "queued"}
            </span>
            {crawl.duration_s !== null && (
              <span>· {formatDuration(crawl.duration_s)}</span>
            )}
          </div>
          {crawl.error && (
            <div className="mt-2 rounded bg-red-500/10 px-2 py-1 text-[11px] text-red-700 dark:text-red-400">
              {crawl.error}
            </div>
          )}
        </div>
        {(crawl.status === "queued" || crawl.status === "running") && (
          <div className="flex shrink-0 items-center gap-1.5">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onPause(); }}
              disabled={pause.isPending}
              title="Pause (preserves config for Resume; no save-state in Frog 23.x)"
              className="rounded-md border border-amber-500/40 bg-background px-2 py-1 text-xs text-amber-700 hover:bg-amber-500/10 disabled:opacity-50 dark:text-amber-300"
            >
              <Pause className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onCancel(); }}
              disabled={cancel.isPending}
              title="Cancel"
              className="rounded-md border border-red-500/40 bg-background px-2 py-1 text-xs text-red-600 hover:bg-red-500/10 disabled:opacity-50 dark:text-red-400"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        )}
        {crawl.status === "paused" && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onResume(); }}
            disabled={resume.isPending}
            title="Resume (creates a new crawl with the same config)"
            className="shrink-0 rounded-md border border-emerald-500/40 bg-background px-2 py-1 text-xs text-emerald-700 hover:bg-emerald-500/10 disabled:opacity-50 dark:text-emerald-300"
          >
            <Play className="h-3 w-3" />
          </button>
        )}
      </div>
    </Card>
  );
}


export function StatusPill({ status }: { status: Crawl["status"] }) {
  const map: Record<Crawl["status"], { label: string; cls: string; icon?: typeof Check }> = {
    queued:    { label: "queued",    cls: "bg-amber-500/10 text-amber-700 dark:text-amber-400" },
    running:   { label: "running",   cls: "bg-blue-500/10 text-blue-700 dark:text-blue-400", icon: Loader2 },
    completed: { label: "completed", cls: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400", icon: Check },
    timed_out: { label: "timed out", cls: "bg-amber-500/10 text-amber-700 dark:text-amber-400", icon: Pause },
    failed:    { label: "failed",    cls: "bg-red-500/10 text-red-700 dark:text-red-400", icon: X },
    cancelled: { label: "cancelled", cls: "bg-gray-500/10 text-gray-700 dark:text-gray-400", icon: X },
    paused:    { label: "paused",    cls: "bg-amber-500/10 text-amber-700 dark:text-amber-400", icon: Pause },
  };
  const { label, cls, icon: Icon } = map[status];
  return (
    <span className={cn(
      "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium",
      cls,
    )}>
      {Icon && <Icon className={cn("h-3 w-3", status === "running" && "animate-spin")} />}
      {label}
    </span>
  );
}


function Mini({ icon: Icon, label, value, kind }: {
  icon?: typeof Globe; label: string; value: number | string | null;
  kind?: "warn";
}) {
  return (
    <div className="flex items-center gap-1.5">
      {Icon && <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
      <span className="text-muted-foreground">{label}:</span>
      <span className={cn("font-medium", kind === "warn" && "text-amber-700 dark:text-amber-400")}>
        {value ?? "—"}
      </span>
    </div>
  );
}


/**
 * Live progress strip for in-flight crawls. Opens an NDJSON stream
 * from the backend (which SSH-tails forge's frog.stdout.log), parses
 * SpiderProgress events, and renders the latest snapshot.
 *
 * Renders inside the CrawlList row beneath the static counters. Closes
 * automatically when the crawl reaches a terminal state.
 */
function LiveProgressStrip({ crawlId }: { crawlId: number }) {
  const p = useCrawlProgressStream(crawlId);
  // Total discovered universe = completed + waiting (Frog's denominator)
  const total = (p.completed ?? 0) + (p.waiting ?? 0);
  const pct = total > 0 ? ((p.completed ?? 0) / total) * 100 : 0;
  const queueGrowing = (p.discovery_per_s ?? 0) > (p.rate_url_per_s ?? 0);

  if (p.error) {
    return (
      <div className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-[11px] text-amber-700 dark:text-amber-400">
        Live progress unavailable: {p.error}
      </div>
    );
  }

  if (!p.connected) {
    return (
      <div className="mt-2 flex items-center gap-2 text-[11px] text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
        Connecting to live progress stream…
      </div>
    );
  }

  // Connected but no SpiderProgress lines yet (very-early crawl OR Frog
  // is in setup/exports phase — no SpiderProgress events emitted)
  if (p.completed == null) {
    return (
      <div className="mt-2 flex items-center gap-2 text-[11px] text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
        Waiting for first SpiderProgress event…
      </div>
    );
  }

  return (
    <div className="mt-2 space-y-1">
      <div className="flex items-center gap-3 text-[11px]">
        <span className="font-mono text-foreground">
          {(p.completed ?? 0).toLocaleString()}
          {" / "}
          {total.toLocaleString()}
          {queueGrowing && (
            <span className="ml-1 text-amber-600 dark:text-amber-400" title="Queue growing faster than completion — link graph not yet saturated">
              ↗
            </span>
          )}
        </span>
        <span className="inline-flex items-center gap-1 text-muted-foreground">
          <Cpu className="h-3 w-3" /> {p.active ?? 0}
        </span>
        <span className="inline-flex items-center gap-1 text-muted-foreground">
          <Gauge className="h-3 w-3" /> {p.rate_url_per_s ?? "—"}/s
        </span>
        <span className="inline-flex items-center gap-1 text-muted-foreground">
          <Hourglass className="h-3 w-3" /> ETA {formatEtaSeconds(p.eta_seconds)}
        </span>
        {p.done && (
          <span className="text-emerald-600 dark:text-emerald-400">
            ✓ {p.doneStatus}
          </span>
        )}
      </div>
      <div className="h-1 w-full overflow-hidden rounded bg-muted">
        <div
          className="h-full bg-blue-500 transition-all"
          style={{ width: `${Math.min(100, Math.max(2, pct))}%` }}
        />
      </div>
    </div>
  );
}


export function formatDuration(s: number): string {
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  const h = Math.floor(s / 3600);
  const m = Math.round((s - h * 3600) / 60);
  return m ? `${h}h ${m}m` : `${h}h`;
}


export function formatDateAgo(iso: string): string {
  const t = new Date(iso).getTime();
  const now = Date.now();
  const sec = Math.round((now - t) / 1000);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.round(sec / 3600)}h ago`;
  return `${Math.round(sec / 86400)}d ago`;
}


// ─── Schedule card ────────────────────────────────────────────────────

const SCHEDULE_PRESETS: { label: string; cron: string; desc: string }[] = [
  { label: "Weekly (Mon 04:00)",  cron: "0 4 * * 1",   desc: "Once a week — light cadence; right for stable sites" },
  { label: "Every 3 days (04:00)", cron: "0 4 */3 * *", desc: "Tighter cadence — right for actively-updated sites" },
  { label: "Daily (04:00)",        cron: "0 4 * * *",   desc: "Maximum cadence — right for fast-moving content" },
];

function CrawlScheduleCard({ propertyId, current, lastFire }: {
  propertyId: number;
  current: string | null;
  lastFire: string | null;
}) {
  const update = useUpdateProperty();
  const [custom, setCustom] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Distinguish preset vs custom
  const matchedPreset = SCHEDULE_PRESETS.find((p) => p.cron === current);
  const isCustomActive = current !== null && !matchedPreset;

  const setSchedule = async (cron: string | null) => {
    setError(null);
    try {
      await update.mutateAsync({
        id: propertyId,
        body: { crawl_schedule: cron ?? "" },  // empty string = clear
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed to update schedule");
    }
  };

  return (
    <Card>
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="flex items-center gap-1.5">
              <CalendarClock className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-medium">Scheduled crawls</h3>
              {current ? (
                <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-400">
                  ON
                </span>
              ) : (
                <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                  OFF
                </span>
              )}
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              System cron fires <code className="font-mono">run_scheduled_crawls.py</code> hourly;
              matching properties get a fresh Frog crawl automatically.
              {lastFire && <> Last fire: <span className="font-medium">{formatDateAgo(lastFire)}</span>.</>}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {SCHEDULE_PRESETS.map((p) => {
            const active = matchedPreset?.cron === p.cron;
            return (
              <button key={p.cron} type="button"
                onClick={() => setSchedule(p.cron)}
                disabled={update.isPending}
                title={p.desc}
                className={cn(
                  "rounded-md border px-2.5 py-1.5 text-xs",
                  active
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-input bg-background hover:bg-accent",
                  update.isPending && "opacity-50",
                )}>
                {active && <Check className="mr-1 inline h-3 w-3" />}
                {p.label}
              </button>
            );
          })}
          <button type="button"
            onClick={() => setSchedule(null)}
            disabled={update.isPending || current === null}
            className={cn(
              "rounded-md border px-2.5 py-1.5 text-xs",
              current === null
                ? "border-input bg-muted/50 text-muted-foreground"
                : "border-red-500/40 bg-background text-red-700 hover:bg-red-500/10 dark:text-red-400",
              update.isPending && "opacity-50",
            )}>
            <X className="mr-1 inline h-3 w-3" />
            Disable
          </button>
        </div>

        {/* Custom cron — only show if a non-preset value is set OR user wants to type one */}
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">Custom:</span>
          <input type="text"
            placeholder={isCustomActive ? current ?? "" : "0 4 * * 1"}
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            className="flex-1 rounded-md border border-input bg-background px-2 py-1 font-mono text-xs"
          />
          <button type="button"
            onClick={() => { if (custom.trim()) setSchedule(custom.trim()); }}
            disabled={update.isPending || !custom.trim()}
            className="rounded-md border border-input bg-background px-2 py-1 text-xs hover:bg-accent disabled:opacity-50">
            Apply
          </button>
          {isCustomActive && (
            <span className="font-mono text-[10px] text-muted-foreground">
              now: <span className="text-foreground">{current}</span>
            </span>
          )}
        </div>

        {error && (
          <div className="rounded-md border border-red-500/40 bg-red-500/10 px-2 py-1 text-xs text-red-700 dark:text-red-400">
            {error}
          </div>
        )}
      </div>
    </Card>
  );
}
